import { z } from 'zod';

/**
 * Type of preparation work, from the master file columns F–K (owner, 2026-11-03, Q24):
 * `inspection` (Insp) physical check on board · `request` (Reqst) the inspector will ask to see a
 * document, record or demonstration · `policy` a procedure is needed in the SMS / IMS ·
 * `training` (Train) crew training or drill · `paper` documents and records · `company` action or
 * supply from the office. Order = column order. Database enum `risq_work_type`.
 */
export const RISQ_WORK_TYPES = [
  'inspection',
  'request',
  'policy',
  'training',
  'paper',
  'company',
] as const;
export type RisqWorkType = (typeof RISQ_WORK_TYPES)[number];
export const risqWorkTypeSchema = z.enum(RISQ_WORK_TYPES);

/**
 * When to prepare, from the master file columns L–O (owner, 2026-11-03, Q24): `six_months` /
 * `three_months` (6-m / 3-m) start 6 / 3 months before the inspection · `last` in the last days
 * before it · `final_24h` (C/L) in the last 24 hours ("Final checks", Task 6b).
 * Database enum `risq_prep_timing`.
 */
export const RISQ_PREP_TIMINGS = ['six_months', 'three_months', 'last', 'final_24h'] as const;
export type RisqPrepTiming = (typeof RISQ_PREP_TIMINGS)[number];
export const risqPrepTimingSchema = z.enum(RISQ_PREP_TIMINGS);

/** Company layer notes on a question (PLAN §2.4). Database enum `risq_company_note_kind`. */
export const RISQ_COMPANY_NOTE_KINDS = ['ims_reference', 'office_remark'] as const;
export type RisqCompanyNoteKind = (typeof RISQ_COMPANY_NOTE_KINDS)[number];
export const risqCompanyNoteKindSchema = z.enum(RISQ_COMPANY_NOTE_KINDS);

/**
 * Who reads a company note: `company` = everyone with a role in the company, `office` = Admin and
 * DPA only. Database enum `risq_note_audience`.
 */
export const RISQ_NOTE_AUDIENCES = ['company', 'office'] as const;
export type RisqNoteAudience = (typeof RISQ_NOTE_AUDIENCES)[number];
export const risqNoteAudienceSchema = z.enum(RISQ_NOTE_AUDIENCES);

/** Audience of each note kind: remarks for the company are never shown to the crew. */
export const RISQ_NOTE_KIND_AUDIENCE: Readonly<Record<RisqCompanyNoteKind, RisqNoteAudience>> = {
  ims_reference: 'company',
  office_remark: 'office',
};
